import React from 'react';
import { AlertTriangle, TrendingUp, TrendingDown, Minus, ChevronUp, Flame, Droplets, Moon, Brain, Heart, Sparkles } from 'lucide-react';

const IMPACT_COLORS = {
  high: { bg: 'bg-[#A388EE]/15', text: 'text-[#A388EE]', dot: 'bg-[#A388EE]' },
  moderate: { bg: 'bg-[#E5C07B]/15', text: 'text-[#9A7B2E]', dot: 'bg-[#E5C07B]' },
  low: { bg: 'bg-[#4ADE80]/15', text: 'text-[#159B4D]', dot: 'bg-[#4ADE80]' },
};

const FACTOR_ICONS = {
  'Acne Severity': Flame, 'Acne Spread': AlertTriangle, 'Stress Level': Brain,
  'Cortisol (Stress)': Brain, 'Gut Inflammation': Heart, 'Sleep Deficit': Moon,
  'Sleep Quality': Moon, 'Sleep & Hormones': Moon, 'Dehydration': Droplets, 'Low Hydration': Droplets,
  'Menstrual Cycle': Heart, 'Menstrual Phase': Heart, 'Hormone Cycle': Heart, 'Inflammatory Foods': AlertTriangle,
  'Gut Irritant Foods': AlertTriangle, 'Bloating': Heart, 'Luteal Phase': Heart, 'Jawline / Chin Acne': Sparkles,
};

const LEVEL_COLORS = { low: 'text-[#A388EE]', moderate: 'text-[#9A7B2E]', high: 'text-[#159B4D]' };
const TREND_ICONS = {
  improving: <TrendingUp className="w-4 h-4 text-[#4ADE80]" />,
  worsening: <TrendingDown className="w-4 h-4 text-[#A388EE]" />,
  neutral: <Minus className="w-4 h-4 text-[#8C7A76]" />,
};

const MetricPanel = ({ analysis, onClose, testId }) => {
  if (!analysis) return null;
  return (
    <div className="mt-4 bg-[#FFF2EC] rounded-xl p-4 animate-fade-in" data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className={`text-sm font-bold ${LEVEL_COLORS[analysis.level] || 'text-[#8C7A76]'}`}>
            {analysis.level_label || 'Unknown'}
          </span>
          {analysis.trend && (
            <span className="flex items-center gap-1 text-xs text-[#8C7A76]">
              {TREND_ICONS[analysis.trend]} {analysis.trend}
            </span>
          )}
        </div>
        <button onClick={onClose} className="text-[#8C7A76] hover:text-[#1A1514]" data-testid={`${testId}-close`}>
          <ChevronUp className="w-4 h-4" />
        </button>
      </div>

      {/* Cycle phase banner (hormone panel only) */}
      {analysis.phase_label && analysis.phase !== 'unknown' && (
        <div className="bg-white rounded-lg p-3 mb-3">
          <p className="text-xs font-bold text-[#A388EE]">
            {analysis.phase_label}{analysis.cycle_day ? ` · Day ${analysis.cycle_day}` : ''}
          </p>
          <p className="text-[11px] text-[#8C7A76] mt-0.5 leading-relaxed">{analysis.phase_description}</p>
        </div>
      )}

      {/* Predictive alert */}
      {analysis.prediction && (
        <div className="bg-[#A388EE]/12 border border-[#A388EE]/30 rounded-lg p-3 mb-3 flex items-start gap-2" data-testid="hormone-prediction">
          <AlertTriangle className="w-4 h-4 text-[#A388EE] flex-shrink-0 mt-0.5" />
          <p className="text-[11px] text-[#1A1514] leading-relaxed">{analysis.prediction.message}</p>
        </div>
      )}

      <p className="text-xs text-[#8C7A76] mb-3 leading-relaxed">{analysis.explanation}</p>

      {analysis.factors?.length > 0 && (
        <div className="space-y-2 mb-3">
          <p className="text-xs font-semibold text-[#1A1514] uppercase tracking-wider">What's driving this</p>
          {analysis.factors.map((factor) => {
            const colors = IMPACT_COLORS[factor.impact] || IMPACT_COLORS.moderate;
            const Icon = FACTOR_ICONS[factor.name] || AlertTriangle;
            return (
              <div key={factor.name} className={`${colors.bg} rounded-lg p-3`}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <span className={colors.text}><Icon className="w-4 h-4" /></span>
                    <span className="text-xs font-semibold text-[#1A1514]">{factor.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-mono text-[#8C7A76]">{factor.value}</span>
                    <span className={`w-2 h-2 rounded-full ${colors.dot}`} />
                  </div>
                </div>
                <p className="text-[11px] text-[#8C7A76] leading-relaxed">{factor.detail}</p>
              </div>
            );
          })}
        </div>
      )}

      {analysis.tips?.length > 0 && (
        <div className="bg-white rounded-lg p-3">
          <p className="text-xs font-semibold text-[#FF6B4A] mb-2">What you can do</p>
          <ul className="space-y-1.5">
            {analysis.tips.map((tip) => (
              <li key={tip} className="text-[11px] text-[#8C7A76] flex items-start gap-2">
                <span className="text-[#4ADE80] mt-0.5">&#9679;</span>
                {tip}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default MetricPanel;
